import {Platform} from 'src/interfaces/Platform.interface'
import {BaseNames} from 'src/marketConnect/helpers'

export const ERP_NATURES = ['ERP', 'ERP_SECONDARY'] as const
export const CONNECTION_ROLES = ['PRIMARY', 'ACCOUNTING', 'SHIPPING', 'ERP', 'OMS'] as const
export type ConnectionRole = (typeof CONNECTION_ROLES)[number]
export type ConnectionChoice = ConnectionRole | 'SECONDARY'

export const MULTIPLE_CONNECTION_ALLOWED_ROLES: ConnectionRole[] = ['SHIPPING']
export const PRIMARY_CONNECTION_BLOCKED_BY_OMS = true

export const SHIPPHQ_PLATFORM_NAME = 'SHIPPHQ'

export const SHIPPHQ_ADMIN_PORTAL_CONNECT_MESSAGE =
  "ShippHQ can only be connected from ShippHQ Admin portal. Go to ShippHQ Admin Portal of your's to initiate the connection from there."

export const isShippHqAdminPortalOnlyPlatform = (platformName?: string | null) =>
  `${platformName ?? ''}`.toUpperCase() === SHIPPHQ_PLATFORM_NAME

export type ConnectionErrorContent = {
  title: string
  message: string
}

export type ConnectionRoleError = string | ConnectionErrorContent

export const PRIMARY_BLOCKED_BY_OMS_ERROR: ConnectionErrorContent = {
  title: 'Action Required',
  message:
    `You cannot connect a ${BaseNames.PRIMARY_Name.toLowerCase()} channel while Commercium is active as your Order Management System (OMS). To change your architecture setup, please contact our support team.`,
}

export const PRIMARY_DUPLICATE_ERROR: ConnectionErrorContent = {
  title: 'Connection Limit Reached',
  message:
    `You can only have one active ${BaseNames.PRIMARY_Name} platform at a time. To swap platforms, please Disconnect the existing ${BaseNames.PRIMARY_Name} Platform or contact support.`,
}

export const formatConnectionErrorForToast = (content: ConnectionErrorContent): string =>
  `${content.title}: ${content.message}`

export const ROLE_DUPLICATE_MESSAGES: Record<ConnectionRole, string> = {
  PRIMARY: formatConnectionErrorForToast(PRIMARY_DUPLICATE_ERROR),
  ACCOUNTING:
    'You cannot connect this platform because an Accounting Channel has already been activated. For more information, please contact support.',
  SHIPPING: 'You have already activated a shipping channel.',
  ERP:
    'You cannot connect this platform because an ERP Channel has already been activated. For more information, please contact support.',
  OMS:
    'You cannot connect this platform because an OMS Channel has already been activated. For more information, please contact support.',
}

export const ROLE_DUPLICATE_POPUP_MESSAGES: Record<ConnectionRole, string> = {
  PRIMARY: PRIMARY_DUPLICATE_ERROR.message,
  ACCOUNTING:
    "You can't connect this platform because an Accounting Channel has already been activated. For more information, please contact support.",
  SHIPPING:
    "You can't connect this platform because a Shipping Channel has already been activated. For more information, please contact support.",
  ERP:
    "You can't connect this platform because an ERP Channel has already been activated. For more information, please contact support.",
  OMS:
    "You can't connect this platform because an OMS Channel has already been activated. For more information, please contact support.",
}

export const ROLE_CHOICE_CONFIG: Record<string, ConnectionChoice[]> = {
  BOTH: ['PRIMARY', 'SECONDARY'],
  ACCOUNTING_SECONDARY: ['ACCOUNTING', 'SECONDARY'],
  SHIPPING_SECONDARY: ['SHIPPING', 'SECONDARY'],
  ERP_SECONDARY: ['ERP', 'SECONDARY'],
  OMS_SECONDARY: ['OMS', 'SECONDARY'],
}

export const ROLE_CHOICE_CONTENT: Record<ConnectionChoice, {label: string; description: string}> = {
  PRIMARY: {
    label: `Make ${BaseNames.PRIMARY_Name} Channel`,
    description:
      'Sync products, inventory and prices to other channels, and use this channel as the main order source.',
  },
  ACCOUNTING: {
    label: 'Make Accounting Channel',
    description: 'Use this channel for accounting sync and accounting order status updates.',
  },
  SHIPPING: {
    label: 'Make Shipping Channel',
    description: 'Use this channel for shipping sync, shipment status, labels and fulfillment updates.',
  },
  ERP: {
    label: 'Make ERP Channel',
    description: 'Use this channel as the ERP system for order and operational sync.',
  },
  OMS: {
    label: 'Make OMS Channel',
    description: 'Use this channel as the OMS source for products, prices, inventory and orders.',
  },
  SECONDARY: {
    label: `Make ${BaseNames.SECONDARY_Name} Channel`,
    description:
      `Receive products, inventory and prices from the ${BaseNames.PRIMARY_Name.toLowerCase()} channel, and send orders back for fulfillment.`,
  },
}

export const ONBOARDING_SECONDARY_LIMIT_MESSAGE =
  'During onboarding you can connect only one destination channel at a time. Disconnect the current destination channel before connecting another.'

export type ConnectionChannelState = {
  hasPrimaryChannel: boolean
  hasAccountingChannel: boolean
  hasShippingChannel: boolean
  hasErpChannel: boolean
  hasOmsChannel: boolean
  hasTicketChannel: boolean
  hasSecondaryChannel: boolean
}

export type ConnectionNotice = {
  type: 'warning' | 'danger'
  message: string
}

export const normalizeNature = (value?: string | null) => (value ?? '').toUpperCase()

export const isTicketType = (platform?: Partial<Platform> | null) =>
  `${platform?.type ?? ''}`.toUpperCase() === 'TICKET'

export const isSecondaryConnectedPlatform = (platform: any): boolean =>
  Boolean(
    platform &&
      !platform.isMainStore &&
      !platform.isAccountingStore &&
      !platform.isShippingStore &&
      !platform.isErpStore &&
      !platform.isOmsStore &&
      !platform.isTicketStore
  )

export const isSourceConnectedPlatform = (platform: any): boolean =>
  Boolean(platform && !isSecondaryConnectedPlatform(platform))

export const findConnectedSourceRecord = (
  platforms: any[] = [],
  preferredName?: string | null
): any | null => {
  if (!platforms.length) return null

  if (preferredName) {
    const matched = platforms.find(
      (p) =>
        (p?.name || '').toUpperCase() === preferredName.toUpperCase() &&
        isSourceConnectedPlatform(p)
    )
    if (matched) return matched
  }

  const sourcePlatforms = platforms.filter(isSourceConnectedPlatform)
  return (
    sourcePlatforms.find((p) => p.isMainStore) ||
    sourcePlatforms.find((p) => p.isOmsStore) ||
    sourcePlatforms.find((p) => p.isAccountingStore) ||
    sourcePlatforms.find((p) => p.isShippingStore) ||
    sourcePlatforms.find((p) => p.isErpStore) ||
    sourcePlatforms.find((p) => p.isTicketStore) ||
    sourcePlatforms[0] ||
    null
  )
}

export const findConnectedSecondaryRecord = (
  platforms: any[] = [],
  preferredName?: string | null
): any | null => {
  if (!platforms.length) return null

  if (preferredName) {
    const matched = platforms.find(
      (p) =>
        (p?.name || '').toUpperCase() === preferredName.toUpperCase() &&
        isSecondaryConnectedPlatform(p)
    )
    if (matched) return matched
  }

  return platforms.find(isSecondaryConnectedPlatform) ?? null
}

export const buildConnectionChannelState = (params: {
  mainStoreData?: any
  accountingStoreData?: any
  shippingStoreData?: any
  erpStoreData?: any
  omsStoreData?: any
  ticketStoreData?: any
  fetchedConnectedPlatformData?: any[]
}): ConnectionChannelState => {
  const {
    mainStoreData,
    accountingStoreData,
    shippingStoreData,
    erpStoreData,
    omsStoreData,
    ticketStoreData,
    fetchedConnectedPlatformData,
  } = params

  const hasPrimaryChannel =
    Boolean(mainStoreData) || fetchedConnectedPlatformData?.some((item) => item?.isMainStore) === true
  const hasAccountingChannel =
    Boolean(accountingStoreData) ||
    fetchedConnectedPlatformData?.some((item) => item?.isAccountingStore) === true
  const hasShippingChannel =
    Boolean(shippingStoreData) ||
    fetchedConnectedPlatformData?.some((item) => item?.isShippingStore) === true
  const hasErpChannel =
    Boolean(erpStoreData) || fetchedConnectedPlatformData?.some((item) => item?.isErpStore) === true
  const hasOmsChannel =
    Boolean(omsStoreData) || fetchedConnectedPlatformData?.some((item) => item?.isOmsStore) === true
  const hasTicketChannel =
    Boolean(ticketStoreData) ||
    fetchedConnectedPlatformData?.some((item) => item?.isTicketStore) === true
  const hasSecondaryChannel =
    fetchedConnectedPlatformData?.some((item) => isSecondaryConnectedPlatform(item)) === true

  return {
    hasPrimaryChannel,
    hasAccountingChannel,
    hasShippingChannel,
    hasErpChannel,
    hasOmsChannel,
    hasTicketChannel,
    hasSecondaryChannel,
  }
}

export const isConfiguredConnectionRole = (role: string): role is ConnectionRole =>
  CONNECTION_ROLES.includes(role as ConnectionRole)

export const roleAllowsMultiple = (role: string) =>
  isConfiguredConnectionRole(role) && MULTIPLE_CONNECTION_ALLOWED_ROLES.includes(role)

export const roleIsLimited = (role: string) =>
  isConfiguredConnectionRole(role) && !roleAllowsMultiple(role)

export const getConnectionRoleChoices = (platform: Platform): ConnectionChoice[] =>
  ROLE_CHOICE_CONFIG[normalizeNature(platform.nature)] ?? []

export const shouldAskConnectionRole = (platform: Platform) =>
  getConnectionRoleChoices(platform).length > 1

export const getDefaultConnectionRoleChoice = (
  platform: Platform,
  channelState: ConnectionChannelState
): ConnectionChoice => {
  const choices = getConnectionRoleChoices(platform)
  const preferredRole = choices[0] ?? 'SECONDARY'
  const secondaryRole = choices.includes('SECONDARY') ? 'SECONDARY' : preferredRole
  const roleConnectionExists = {
    PRIMARY: channelState.hasPrimaryChannel,
    ACCOUNTING: channelState.hasAccountingChannel,
    SHIPPING: channelState.hasShippingChannel,
    ERP: channelState.hasErpChannel,
    OMS: channelState.hasOmsChannel,
  } as const

  if (preferredRole === 'PRIMARY' && (channelState.hasPrimaryChannel || channelState.hasOmsChannel)) {
    return secondaryRole
  }

  if (
    isConfiguredConnectionRole(preferredRole) &&
    roleIsLimited(preferredRole) &&
    roleConnectionExists[preferredRole]
  ) {
    return secondaryRole
  }

  return preferredRole
}

export const resolveConnectNature = (
  platform: Platform,
  channelState: ConnectionChannelState,
  options?: {activeNature?: string; wizardRole?: 'source' | 'destination'; skipTypeFilterTabs?: string[]}
): string => {
  const activeNature = normalizeNature(options?.activeNature)
  const platformNature = normalizeNature(platform.nature)
  const wizardRole = options?.wizardRole
  const skipTabs = new Set((options?.skipTypeFilterTabs ?? []).map((tab) => tab.toUpperCase()))

  if (wizardRole === 'destination') {
    if (shouldAskConnectionRole(platform)) {
      return getDefaultConnectionRoleChoice(platform, channelState)
    }
    if (platformNature === 'PRIMARY') {
      return 'SECONDARY'
    }
    return resolveConnectNature(platform, channelState, {activeNature})
  }

  if (activeNature && activeNature !== 'ALL' && !skipTabs.has(activeNature)) {
    return activeNature
  }

  switch (platformNature) {
    case 'PRIMARY':
      return 'PRIMARY'
    case 'BOTH':
      return channelState.hasPrimaryChannel ? 'SECONDARY' : 'PRIMARY'
    case 'SECONDARY':
      return 'SECONDARY'
    case 'ACCOUNTING':
      return 'ACCOUNTING'
    case 'ACCOUNTING_SECONDARY':
      return channelState.hasAccountingChannel ? 'SECONDARY' : 'ACCOUNTING'
    case 'SHIPPING':
      return 'SHIPPING'
    case 'SHIPPING_SECONDARY':
      return channelState.hasShippingChannel ? 'SECONDARY' : 'SHIPPING'
    case 'OMS':
      return 'OMS'
    case 'OMS_SECONDARY':
      return channelState.hasOmsChannel ? 'SECONDARY' : 'OMS'
    case 'ERP':
      return 'ERP'
    case 'ERP_SECONDARY':
      return channelState.hasErpChannel ? 'SECONDARY' : 'ERP'
    default:
      return activeNature || platformNature
  }
}

export type ConnectionValidationResult = {
  ok: boolean
  error?: string
  errorContent?: ConnectionErrorContent
  notice?: ConnectionNotice
}

export const validateConnectionRole = (
  platform: Platform,
  connectNature: string,
  channelState: ConnectionChannelState,
  options?: {
    showInlineError?: boolean
    enforceSingleSecondary?: boolean
    platformAlreadyConnected?: boolean
  }
): ConnectionValidationResult => {
  const platformNature = normalizeNature(platform.nature)
  const isPrimaryOnly = platformNature === 'PRIMARY'
  const isSecondaryOnly = platformNature === 'SECONDARY'
  const showInlineError = options?.showInlineError ?? false
  const roleConnectionExists = {
    PRIMARY: channelState.hasPrimaryChannel,
    ACCOUNTING: channelState.hasAccountingChannel,
    SHIPPING: channelState.hasShippingChannel,
    ERP: channelState.hasErpChannel,
    OMS: channelState.hasOmsChannel,
  } as const

  if (
    options?.enforceSingleSecondary &&
    connectNature === 'SECONDARY' &&
    channelState.hasSecondaryChannel &&
    !options.platformAlreadyConnected
  ) {
    return {ok: false, error: ONBOARDING_SECONDARY_LIMIT_MESSAGE}
  }

  if (connectNature === 'PRIMARY') {
    if (PRIMARY_CONNECTION_BLOCKED_BY_OMS && channelState.hasOmsChannel) {
      return {
        ok: false,
        error: formatConnectionErrorForToast(PRIMARY_BLOCKED_BY_OMS_ERROR),
        errorContent: PRIMARY_BLOCKED_BY_OMS_ERROR,
      }
    }

    if (
      roleIsLimited(connectNature) &&
      roleConnectionExists[connectNature as keyof typeof roleConnectionExists]
    ) {
      return {
        ok: false,
        error: formatConnectionErrorForToast(PRIMARY_DUPLICATE_ERROR),
        errorContent: PRIMARY_DUPLICATE_ERROR,
      }
    }

    if (isPrimaryOnly) {
      return {
        ok: true,
        notice: {
          type: 'warning',
          message: `This channel will be connected as ${BaseNames.PRIMARY_Name.toLowerCase()}.`,
        },
      }
    }
  }

  if (
    roleIsLimited(connectNature) &&
    roleConnectionExists[connectNature as keyof typeof roleConnectionExists]
  ) {
    return {
      ok: false,
      error: showInlineError
        ? ROLE_DUPLICATE_POPUP_MESSAGES[connectNature as ConnectionRole]
        : ROLE_DUPLICATE_MESSAGES[connectNature as ConnectionRole],
    }
  }

  if (isConfiguredConnectionRole(connectNature) && connectNature !== 'PRIMARY') {
    const roleLabel = connectNature.toLowerCase()
    return {ok: true, notice: {type: 'warning', message: `This channel will be connected as ${roleLabel}.`}}
  }

  if (connectNature === 'SECONDARY') {
    if (
      isSecondaryOnly ||
      ['ACCOUNTING_SECONDARY', 'SHIPPING_SECONDARY', 'ERP_SECONDARY', 'OMS_SECONDARY'].includes(
        platformNature
      )
    ) {
      return {
        ok: true,
        notice: {
          type: 'warning',
          message: `This channel will be connected as ${BaseNames.SECONDARY_Name.toLowerCase()}.`,
        },
      }
    }
  }

  return {ok: true}
}

export type BuildConnectPlatformPayloadOptions = {
  extras?: Record<string, any>
  channelState?: ConnectionChannelState
}

export const buildConnectPlatformPayload = (
  platform: Platform,
  dataCollector: any[],
  friendlyName: string,
  connectNature: string,
  connection_type?: string,
  options?: BuildConnectPlatformPayloadOptions
) => {
  const find = (key: string) => dataCollector.find((item: any) => item.name === key)?.value ?? null
  const extras = options?.extras ?? {}
  const channelState = options?.channelState
  const isErpNature = (value?: string | null) =>
    ERP_NATURES.includes(normalizeNature(value) as (typeof ERP_NATURES)[number])
  const isOmsNature = (value?: string | null) =>
    ['OMS', 'OMS_SECONDARY'].includes(normalizeNature(value))
  const isEbay = platform.name === 'EBAY'

  let isMainStore = platform.nature === 'PRIMARY' || connectNature === 'PRIMARY'
  let isAccountingStore =
    ['ACCOUNTING', 'ACCOUNTING_SECONDARY'].includes(platform?.nature ?? '') &&
    connectNature === 'ACCOUNTING'
  let isShippingStore =
    ['SHIPPING', 'SHIPPING_SECONDARY'].includes(platform?.nature ?? '') &&
    connectNature === 'SHIPPING'
  let isErpStore = isErpNature(platform?.nature) && connectNature === 'ERP'
  let isOmsStore = isOmsNature(platform?.nature) || connectNature === 'OMS'
  let isTicketStore = isTicketType(platform)

  if (connectNature === 'ALL') {
    switch (normalizeNature(platform.nature)) {
      case 'PRIMARY':
        isMainStore = true
        isErpStore = false
        break
      case 'SECONDARY':
        isMainStore = false
        isErpStore = false
        break
      case 'SHIPPING':
        isShippingStore = true
        isErpStore = false
        break
      case 'ACCOUNTING':
        isAccountingStore = true
        isErpStore = false
        break
      case 'BOTH':
        isMainStore = channelState ? !channelState.hasPrimaryChannel : true
        isErpStore = false
        break
      case 'SHIPPING_SECONDARY':
        isShippingStore = channelState ? !channelState.hasShippingChannel : true
        isMainStore = false
        isErpStore = false
        break
      case 'ACCOUNTING_SECONDARY':
        isAccountingStore = channelState ? !channelState.hasAccountingChannel : true
        isMainStore = false
        isErpStore = false
        break
      case 'ERP':
        isErpStore = true
        isMainStore = false
        isAccountingStore = false
        isShippingStore = false
        break
      case 'ERP_SECONDARY':
        isErpStore = channelState ? !channelState.hasErpChannel : true
        isMainStore = false
        isAccountingStore = false
        isShippingStore = false
        break
      case 'OMS':
      case 'OMS_SECONDARY':
        isOmsStore = true
        isMainStore = false
        isAccountingStore = false
        isShippingStore = false
        isErpStore = false
        break
      case 'TICKET':
        isTicketStore = true
        isMainStore = false
        isAccountingStore = false
        isShippingStore = false
        isErpStore = false
        break
      default:
        break
    }
  }

  if (connectNature === 'SECONDARY') {
    isMainStore = false
    isAccountingStore = false
    isShippingStore = false
    isErpStore = false
    isOmsStore = false
    isTicketStore = false
  }

  const accountModeValue =
    extras.account_mode ??
    extras.mode ??
    find('account_mode') ??
    find('mode') ??
    ''

  const ebayCountryIso =
    extras.country_iso ?? find('country_iso') ?? ''

  const payload: any = {
    name: platform.name,
    type: platform.type,
    code: platform.name,
    display_name: friendlyName,
    platform_info: platform.id,
    credentials: {
      account_mode: accountModeValue,
      identifier: find('identifier') || find('domain'),
      client_id: find('client_id') || find('api_key'),
      client_secret: find('client_secret') || find('api_secret'),
      token_id: find('token_id'),
      token_secret: find('token_secret'),
      refresh_token: null,
      sftp_hostname: find('sftp_hostname'),
      sftp_username: find('sftp_username'),
      sftp_password: find('sftp_password'),
      country: isEbay ? '' : find('country') || '',
      country_code: isEbay ? '' : find('country_code') || '',
      country_iso: isEbay ? ebayCountryIso : find('country_iso') || '',
      proxies: find('proxies') || '',
      mode: accountModeValue,
      currency: isEbay ? '' : find('currency') || '',
      expiry_at: find('expiry_at') || '',
      country_region: find('country_region') || find('Region') || '',
      application_id: find('application_id') || '',
      version: find('version') || '',
      domain: find('domain') || find('identifier') || '',
      connection_type,
    },
    isConnected: true,
    isActive: true,
    isMainStore,
    isAccountingStore,
    isShippingStore,
    isErpStore,
    isOmsStore,
    isTicketStore,
    isOrderSyncProcessing: false,
    isNewOrderSyncProcessing: false,
    isOrderCreateProcessing: false,
  }

  if (accountModeValue) {
    payload.account_mode = accountModeValue
  }

  return payload
}

export const resolveOnboardingConnectNature = (
  platform: Platform,
  step: 'source' | 'destination'
): string => {
  if (step === 'destination') {
    return 'SECONDARY'
  }

  const platformNature = normalizeNature(platform.nature)
  switch (platformNature) {
    case 'PRIMARY':
    case 'BOTH':
      return 'PRIMARY'
    case 'ACCOUNTING':
    case 'ACCOUNTING_SECONDARY':
      return 'ACCOUNTING'
    case 'SHIPPING':
    case 'SHIPPING_SECONDARY':
      return 'SHIPPING'
    case 'ERP':
    case 'ERP_SECONDARY':
      return 'ERP'
    case 'OMS':
    case 'OMS_SECONDARY':
      return 'OMS'
    default:
      return 'PRIMARY'
  }
}

export const deriveOAuthStoreFlags = (
  platform: Platform | null,
  connectNature?: string,
  legacyIsPrimary?: boolean,
  channelState?: ConnectionChannelState
) => {
  if (!platform) {
    return {
      isMainStore: false,
      isAccountingStore: false,
      isShippingStore: false,
      isErpStore: false,
      isOmsStore: false,
      isTicketStore: false,
    }
  }

  if (connectNature) {
    const payload = buildConnectPlatformPayload(platform, [], '', connectNature, undefined, {
      channelState,
    })
    return {
      isMainStore: payload.isMainStore,
      isAccountingStore: payload.isAccountingStore,
      isShippingStore: payload.isShippingStore,
      isErpStore: payload.isErpStore,
      isOmsStore: payload.isOmsStore,
      isTicketStore: payload.isTicketStore,
    }
  }

  const isPrimary = legacyIsPrimary ?? false
  return {
    isMainStore:
      platform.nature === 'PRIMARY' ? true : platform.nature === 'BOTH' && isPrimary ? true : false,
    isAccountingStore:
      ['ACCOUNTING', 'ACCOUNTING_SECONDARY'].includes(platform.nature ?? '') && isPrimary,
    isShippingStore:
      ['SHIPPING', 'SHIPPING_SECONDARY'].includes(platform.nature ?? '') && isPrimary,
    isErpStore: false,
    isOmsStore: false,
    isTicketStore: isTicketType(platform),
  }
}
